/**
 * Maps helpers. Pure: no DOM, no window, no fetch.
 *
 * The owner is standing in a truck and wants to tap an address to open their
 * maps app with directions. This module builds the URL that does that.
 */

const MAPS_BASE = 'https://www.google.com/maps/search/?api=1&query=';

/**
 * Build a URL that opens `address` in the user's maps app.
 *
 * Returns null when there is nothing worth linking (empty or whitespace-only),
 * so the caller can render plain text instead of a link to nowhere.
 *
 * The address is percent-encoded with encodeURIComponent. This is the
 * invariant that actually matters: a real address like
 * "123 Main St Apt #4, Slidell, LA" contains a '#', which unencoded ends the
 * query string and starts a fragment — the maps app then receives
 * "123 Main St Apt " and silently navigates to the wrong place. '&' and '+'
 * corrupt it in their own ways. Encode, and assert it.
 *
 * One URL works everywhere that matters: on Android and iOS it hands off to
 * the Google Maps app when installed, and falls back to the browser otherwise.
 * Do NOT emit a geo: URI (iOS does not support it) and do NOT emit maps://
 * (Android does not).
 */
export function mapsUrl(address: string): string | null {
  const trimmed = address.trim();
  if (trimmed === '') return null;
  return `${MAPS_BASE}${encodeURIComponent(trimmed)}`;
}
